import { collection, deleteDoc, doc, setDoc, Timestamp, updateDoc } from "firebase/firestore";

import { logAnalyticsEvent } from '../analytics/analytics';
import { useAuth } from "../contextProviders/AuthProvider";
import { useData } from "../contextProviders/DataProvider";
import { firestore } from "../firebase";
import Course from "../types/Course";
import { usgaTeeSetRatingIdsOf } from "../utils/courseUtils";

export const useCourseMutators = () => {
  const { isGod } = useAuth();
  const { isOrgAdmin, userState } = useData();

  const addCourse = async (course: Course): Promise<Course | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found');
      return null;
    }

    if (!isGod) {
      console.error('Permission denied: only admins can add courses');
      return null;
    }

    try {
      // Use provided itemId if available, otherwise generate a new one
      const courseRef = course.itemId
        ? doc(firestore, `course/${course.itemId}`)
        : doc(collection(firestore, 'course'));

      const newCourse = {
        ...course,
        usgaTeeSetRatingIds: usgaTeeSetRatingIdsOf(course.tees ?? []),
        creatorId: userState.userProfile?.itemId,
        createTime: Timestamp.now(),
        itemId: courseRef.id
      };

      // Remove undefined values to prevent Firestore errors
      const cleanedCourse = JSON.parse(JSON.stringify(newCourse));

      await setDoc(courseRef, cleanedCourse);
      logAnalyticsEvent('course_created', {});
      return cleanedCourse;
    } catch (error) {
      console.error('Error adding course:', error);
      return null;
    }
  };

  const deleteCourse = async (course: Course): Promise<void> => {
    if (!isGod) {
      console.error('Permission denied: only admins can delete courses');
      return;
    }

    if (!course.itemId) {
      console.error('Course itemId not found');
      return;
    }

    try {
      const courseRef = doc(firestore, 'course/' + course.itemId);
      await deleteDoc(courseRef);
    } catch (error) {
      console.error('Error deleting course:', error);
    }
  };

  const updateCourse = async (course: Partial<Course>): Promise<void> => {
    if (!course.itemId) {
      console.error('Course itemId not found');
      return;
    }

    // Only check org admin rights if an orgId is specified
    if (!isGod && course.orgId && !isOrgAdmin(course.orgId)) {
      console.error("Permission denied: must be admin of course's organization");
      return;
    }

    try {
      const courseRef = doc(firestore, 'course/' + course.itemId);

      const { itemId: _itemId, ...updates } = course;
      // Keep the flat, queryable USGA tee set id list in step with every tees write.
      if (updates.tees) updates.usgaTeeSetRatingIds = usgaTeeSetRatingIdsOf(updates.tees);

      // Remove undefined values to prevent Firestore errors
      const cleanedUpdates = JSON.parse(JSON.stringify(updates));

      await updateDoc(courseRef, cleanedUpdates);
    } catch (error) {
      console.error('Error updating course:', error);
    }
  };

  return {
    addCourse,
    deleteCourse,
    updateCourse,
  };
};
